import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ErrorWithJSON } from "../types";
import { SmallModelCard, type ModelExample } from "./explore/model-card";

interface SourceModel {
  name: string;
  description: string;
  tags: string[];
  last_updated: string;
  run_count: number;
  // OFFICIAL_MODEL_AUDIT(aron)
  is_official: boolean;
}

interface SimilarModel {
  name: string;
  description: string;
  tags: string[];
  last_updated: string;
  age_days: number;
  vector_similarity: number;
  fts_score: number | null;
  run_count: number;
  cover_image_url?: string | null;
  avatar_url?: string | null;
  model_description?: string;
  score: number;
  has_vector: boolean;
  rerank_score: number | null;
  aa_rank: number | null;
  aa_category: string | null;
  collection_name: string | null;
  collection_rank: number | null;
  // OFFICIAL_MODEL_AUDIT(aron)
  is_official: boolean;
}

export interface RelatedModelsResponse {
  source_model: SourceModel;
  similar_models: SimilarModel[];
  count: number;
  embedding_model: string;
  weights: {
    recencyWeight: number;
    runsWeight: number;
    officialWeight: number;
  };
}

export function useRelatedModelsQuery(
  name: string
): UseQueryResult<RelatedModelsResponse, ErrorWithJSON> {
  return useQuery({
    queryKey: ["related_models", name],
    queryFn: () => fetchRelatedModels(name),
    refetchOnWindowFocus: false,
  });
}

async function fetchRelatedModels(
  name: string
): Promise<RelatedModelsResponse> {
  const res = await fetch(
    `https://replicate-search-prototype-production.replicate.workers.dev/similar-models?model=${name}`
  );

  if (res.ok) {
    return res.json<RelatedModelsResponse>();
  }

  try {
    const errorJson = await res.json<ErrorWithJSON>();
    return Promise.reject({
      ...errorJson,
      status: res.status,
    });
  } catch (e) {
    return Promise.reject({
      detail: "Failed to fetch related models",
      status: res.status,
    });
  }
}

function transformSimilarModelToModelExample(
  model: SimilarModel
): ModelExample {
  const [owner, modelName] = model.name.split("/");

  const formatRunCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}m`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return {
    name: modelName,
    owner,
    coverImage: model.cover_image_url,
    runCount: model.run_count,
    runCountDisplay: formatRunCount(model.run_count),
    description: model.description,
    url: `/${model.name}`,
    // OFFICIAL_MODEL_AUDIT(aron)
    isOfficial: model.is_official,
    avatarImage: model.avatar_url,
  };
}

export default function RelatedModels({
  name,
}: {
  name: string;
}) {
  const { data, isError } = useRelatedModelsQuery(name);

  if (isError || !data?.similar_models.length) {
    return null;
  }

  return (
    <div className="layout-main flex-1">
      <div className="mb-2lh">
        <div
          id="related-models"
          className="border-b border-r8-gray-5 pb-2 mb-lh flex items-center gap-2"
        >
          <h2 className="inline-block text-r8-2xl">Related models</h2>
        </div>
        <RelatedModelsInner
          models={data?.similar_models.map(transformSimilarModelToModelExample)}
        />
      </div>
    </div>
  );
}

function RelatedModelsInner({
  models,
}: {
  models: ModelExample[];
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {models.slice(0, 8).map((model) => (
        <a
          title={`${model.owner}/${model.name}: ${model.description}` || ""}
          key={model.name}
          href={model.url || `/${model.owner}/${model.name}`}
          className="block group no-underline outline-none"
        >
          <SmallModelCard key={model.name} model={model} />
        </a>
      ))}
    </div>
  );
}
